import { Avatar, Box, HStack, Menu, MenuButton, MenuDivider, MenuItem, MenuList, Text, useBreakpointValue } from '@chakra-ui/react';
import { setAccessToken } from '@partiu/api/axios.ts';
import { useProfile } from '@partiu/api/user/hooks.ts';
import React, { useEffect } from 'react';
import { FaBars } from 'react-icons/fa';
import { FaPowerOff } from 'react-icons/fa6';
import { IconType } from 'react-icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeProvider.tsx';
import { setBrowserThemeColor } from '../../utils.ts';
import { DrawerMenu, IMenuLink, IMenuSection, SIDEBAR_EXPANDED_WIDTH, SIDEBAR_RAIL_WIDTH } from './DrawerMenu.tsx';

export interface ITopMenuQuickAction {
  icon: IconType;
  label: string;
  onClick?: () => void;
  path?: string;
}

export const HEADER_HEIGHT = '52px';
const HOME_PATH = '/dashboard';

function findActiveTitle(items: (IMenuLink | IMenuSection)[] | undefined, pathname: string): string | undefined {
  if (!items) return undefined;

  for (const item of items) {
    if ('section' in item) {
      const match = item.items.find((link) => link.path === pathname);
      if (match) return match.display;
    } else if (item.path === pathname) {
      return item.display;
    }
  }

  return undefined;
}

export function TopMenuDash({
  title = '',
  links,
  drawerLinks,
  quickActions,
}: {
  title?: React.ReactNode;
  links?: IMenuLink[];
  drawerLinks?: (IMenuLink | IMenuSection)[];
  quickActions?: ITopMenuQuickAction[];
}) {
  const { setMenuOpen, isMenuOpen, isSidebarCompact, setSidebarCompact, colors, resetTheme } = useTheme();
  const { data: profile } = useProfile();
  const navigate = useNavigate();
  const location = useLocation();
  const isDesktop = useBreakpointValue({ base: false, md: true });
  const sidebarWidth = isSidebarCompact ? SIDEBAR_RAIL_WIDTH : SIDEBAR_EXPANDED_WIDTH;

  useEffect(() => {
    setBrowserThemeColor(colors?.primaryColor);
  }, [colors]);

  const handleLogout = () => {
    resetTheme();
    setAccessToken(null);
    navigate('/');
  };

  const isHome = location.pathname === HOME_PATH;
  const pageTitle = findActiveTitle(drawerLinks ?? links, location.pathname);
  const avatarMenuItems = quickActions ?? [];

  return (
    <>
      <DrawerMenu profile={profile} links={drawerLinks || links} />
      <HStack
        position="fixed"
        top={0}
        left={{ base: 0, md: sidebarWidth }}
        width={{ base: '100%', md: `calc(100% - ${sidebarWidth})` }}
        transition="left 0.2s ease-in-out, width 0.2s ease-in-out"
        height={HEADER_HEIGHT}
        backgroundColor="white"
        borderBottom="1px solid"
        borderColor="gray.200"
        paddingX={{ base: '12px', md: '20px' }}
        zIndex={15}
        spacing={3}
      >
        <Box
          as="button"
          aria-label="Alternar menu"
          onClick={() => (isDesktop ? setSidebarCompact(!isSidebarCompact) : setMenuOpen(!isMenuOpen))}
          display="flex"
          alignItems="center"
          justifyContent="center"
          width="32px"
          height="32px"
          borderRadius="8px"
          flexShrink={0}
          _hover={{ backgroundColor: 'gray.100' }}
        >
          <FaBars size={16} color="#4A5568" />
        </Box>

        {isHome && title ? (
          <Box flexShrink={0}>{title}</Box>
        ) : (
          <Text flex="1" fontSize="sm" fontWeight="600" color="gray.700" noOfLines={1}>
            {pageTitle ?? ''}
          </Text>
        )}

        <Menu>
          <MenuButton
            cursor={'pointer'}
            as={Avatar}
            src={profile?.image}
            boxSize={'32px'}
            overflow={'hidden'}
            flexShrink={0}
            marginLeft="auto"
          />
          <MenuList
            backgroundColor="white"
            border="1px solid"
            borderColor="gray.200"
            boxShadow="0 8px 25px rgba(0,0,0,0.15)"
            borderRadius="12px"
            py={2}
            minW="200px"
          >
            {avatarMenuItems.map((item, index) => {
              const isActive = !!item.path && location.pathname === item.path;
              return (
                <MenuItem
                  key={item.path ?? `${item.label}-${index}`}
                  onClick={item.onClick}
                  icon={React.createElement(item.icon, {
                    color: isActive ? colors.primaryColor : 'gray.500',
                    size: 18,
                  })}
                  backgroundColor={isActive ? `${colors.primaryColor}10` : 'transparent'}
                  color={isActive ? colors.primaryColor : 'gray.700'}
                  fontWeight={isActive ? '700' : '500'}
                  _hover={{
                    backgroundColor: isActive ? `${colors.primaryColor}15` : 'gray.50',
                    transform: 'translateX(2px)',
                  }}
                  fontSize="sm"
                  py={3}
                  px={4}
                  borderRadius="8px"
                  mx={2}
                  my={1}
                >
                  {item.label}
                </MenuItem>
              );
            })}

            {avatarMenuItems.length > 0 && <MenuDivider />}

            <MenuItem
              onClick={handleLogout}
              icon={<FaPowerOff />}
              backgroundColor="transparent"
              color="gray.700"
              fontWeight="500"
              _hover={{
                backgroundColor: 'red.50',
                color: 'red.600',
                transform: 'translateX(2px)',
              }}
              fontSize="sm"
              py={3}
              px={4}
              borderRadius="8px"
              mx={2}
              my={1}
            >
              Sair
            </MenuItem>
          </MenuList>
        </Menu>
      </HStack>
    </>
  );
}
